"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronRight, Globe, Pencil } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { Badge } from "@/components/ui/badge";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";

export function LanguagesDisplay() {
  const { profileData } = useProfileDataStore();
  const data = profileData?.languages_section;
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const { open } = useProfileEditDialogStore();

  if (!data || data.length === 0) return null;

  const displayedLanguages = isExpanded ? data : data.slice(0, 4);
  const hasMore = data.length > 4;

  const getProficiencyColor = (proficiency: string) => {
    switch (proficiency) {
      case "native":
        return "bg-green-500/5";
      case "fluent":
        return "bg-blue-500/5";
      case "conversational":
        return "bg-yellow-500/5";
      default:
        return "bg-gray-500/5";
    }
  };

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-teal-400/50 shadow-2xl backdrop-blur-xl">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-teal-400 to-cyan-600" />
        <div className="absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-gradient-to-tr from-cyan-400 to-teal-500" />
      </div>

      <CardHeader className="relative flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl p-2">
            <Icon className="text-teal-400" height="1.25rem" icon="mdi:language" width="1.25rem" />
          </div>
          {t("profile:languagesSection.title")}
        </motion.h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="bg-foreground/50 hover:bg-foreground/70 absolute top-2 right-2 rounded-xl backdrop-blur-sm"
                size="sm"
                variant="ghostSecondary"
                onClick={() => open("languages")}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{t("common:edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent className="relative space-y-4">
        <div
          className={`grid grid-cols-1 gap-4 sm:grid-cols-${displayedLanguages.length >= 2 ? "2" : "1"}`}
        >
          <AnimatePresence initial={false} mode="wait">
            {displayedLanguages.map((language, index) => (
              <motion.div
                key={language.id}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="relative overflow-hidden rounded-2xl bg-transparent backdrop-blur-sm transition-all duration-300 hover:bg-teal-500/2"
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl p-2">
                        <Globe className="h-4 w-4 text-teal-400" />
                      </div>
                      <h3 className="text-accent-foreground font-semibold">{language.name}</h3>
                    </div>
                    <Badge
                      className={`border-none ${getProficiencyColor(language.proficiency_slug)} text-xs font-medium text-teal-200`}
                      variant="outline"
                    >
                      {language.proficiency_display}
                    </Badge>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {hasMore && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} transition={{ delay: 0.2 }}>
            <Button
              className="mt-4 w-full justify-center gap-2 rounded-xl border-none bg-transparent text-teal-300 hover:bg-teal-500/15"
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span>
                {isExpanded ? t("common:showLess") : t("common:showAll", { count: data.length })}
              </span>
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
              />
            </Button>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
